/**
 * Why the person is looking at the login page.
 *
 * A session can end for six or seven quite different reasons, and until now
 * every one of them produced the same thing: a silent bounce to /login with
 * `?session_expired=true` in the URL that nothing ever read. Somebody signed
 * out because a stranger is using their account saw exactly what somebody
 * whose token expired overnight saw — nothing.
 *
 * The reason arrives as a CODE in the query string, never as a sentence. That
 * URL is craftable by anyone, and a login page that renders text handed to it
 * in a query string is a phishing surface ("Your account is locked, call this
 * number"). Codes map to fixed copy here; an unknown code falls back to the
 * neutral line rather than displaying itself.
 *
 * Codes come from backend/app/core/session_security.py's _REVOKED_MESSAGES.
 * Wording is deliberately not identical to the server's: this is the screen
 * the person is actually standing on, and it can afford to be plainer.
 */

const NOTICES = {
  // The one this whole feature exists for.
  superseded: {
    tone: 'warning',
    text: 'You were signed out because this account was signed in somewhere else. '
        + 'Only one device can be signed in at a time — if that was not you, '
        + 'change your password now.',
  },
  password_changed: {
    tone: 'info',
    text: 'The password on this account was changed, so other devices were signed out. '
        + 'Please sign in with the new password.',
  },
  password_reset: {
    tone: 'info',
    text: 'The password on this account was reset. Please sign in with the new password.',
  },
  admin_revoke: {
    tone: 'info',
    text: 'An administrator ended your session. Please sign in again.',
  },
  fingerprint_mismatch: {
    tone: 'warning',
    text: 'Your session was ended because it was used from a different browser or device.',
  },
  device_fingerprint_mismatch: {
    tone: 'warning',
    text: 'Your session was ended because it was used from a different device.',
  },
  impossible_travel: {
    tone: 'warning',
    text: 'Your session was ended because it was used from a location it could not have '
        + 'travelled to. If that was not you, change your password now.',
  },
  refresh_reuse_detected: {
    tone: 'warning',
    text: 'For your security, every session on this account was signed out. '
        + 'Please sign in again, and change your password if this was not you.',
  },
  no_session: {
    tone: 'info',
    text: 'Please sign in again to continue.',
  },
  logout: {
    tone: 'info',
    text: 'You have been signed out.',
  },
};

const EXPIRED = {
  tone: 'info',
  text: 'Your session has expired. Please sign in again.',
};

/**
 * @param {URLSearchParams} params  the login page's query string
 * @returns {{tone: 'info'|'warning', text: string}|null}
 */
export function sessionEndedNotice(params) {
  const code = params?.get?.('signed_out');
  if (code) return NOTICES[code] || EXPIRED;
  // The legacy flag the client has always set. Kept so an older cached bundle
  // redirecting here still says something.
  if (params?.get?.('session_expired')) return EXPIRED;
  return null;
}

export default sessionEndedNotice;
